import { useNavigate, useLocation } from "react-router-dom";
import { useContext, useMemo, useEffect } from "react";
import { useGetCalendarEvents } from "src/services";
import ical from "ical.js";
import { Box, Typography, Button, Link } from "@mui/material";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute } from "src/services";
import { customEvent } from "src/utils/gtag.js";
import UAParser from "ua-parser-js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import {
  generateGCalReminder,
  getCalendarApiUrl,
  getWebcalFormat,
  useCheckSubscriptionStatus,
  copyCalendarLinkToClipboard,
  handleOpenWebcal,
} from "src/utils/Utils.js";
import InstructionsLayout from "../InstructionsLayout.js";
import { useTheme } from "@emotion/react";
import LoadingAnimation from "src/components/blocks/LoadingAnimation.js";

const VerifyCalendarSubscription = () => {
  const { user } = useContext(AuthContext);
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();
  const location = useLocation();
  const calendar = location.state ? location.state.calendar ?? null : null;
  const isReminder = location.state?.isReminder ?? false;
  const navigate = useNavigate();
  const uaParser = new UAParser();
  const parserResults = uaParser.getResult();
  const isDesktop = !parserResults.device.type;
  const calendarApiUrl = getCalendarApiUrl({ calendar, user });
  const { inGroup } = useCheckSubscriptionStatus({
    user,
    calendar,
  });
  const theme = useTheme();

  const link = useMemo(
    () => calendar?.stanzaLink?.replace("webcal://", "http://") || "",
    [calendar?.stanzaLink]
  );

  const { data: calendarEvents, isLoading: isLoadingCalendarEvents } =
    useGetCalendarEvents(
      {
        link,
        updated: calendar?.updatedAt,
      },
      {
        enabled: !!calendar?.stanzaLink,
      }
    );

  const mostRecentEventDate = useMemo(() => {
    if (!calendarEvents) return null;

    try {
      const jcalData = ical.parse(calendarEvents);
      const comp = new ical.Component(jcalData);
      const vevents = comp.getAllSubcomponents("vevent");

      if (vevents.length === 0) return null;

      const now = new Date();
      let closestEvent = null;
      let smallestDiff = Infinity;

      vevents.forEach((event) => {
        const end = event.getFirstPropertyValue("dtend").toJSDate();
        // Only consider events that have not yet ended
        if (end > now) {
          const diff = end - now;
          if (diff < smallestDiff) {
            smallestDiff = diff;
            closestEvent = event;
          }
        }
      });

      if (!closestEvent) return null;

      const eventDate = closestEvent
        .getFirstPropertyValue("dtstart")
        .toJSDate();
      return eventDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [calendarEvents]);

  const handleConfirm = () => {
    // Track the "Yes, I see it" button click
    customEvent({
      name: "user_confirm_calendar_subscription",
      category: "calendar_subscription",
      label: isReminder ? "confirm_reminder" : "confirm_calendar",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: calendar?.handle,
      },
    });

    navigate(getPageRoute("help", "SUBSCRIBING_CALENDAR_SUCCESS"), {
      state: {
        calendar,
        isReminder,
      },
    });
  };

  const handleResubscribe = () => {
    if (isReminder) {
      // Track the "Download the reminder again" click
      customEvent({
        name: "user_error_download_reminder_again",
        category: "CalendarSubscription",
        label: "Download Reminder Again",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          user_handle: user?.handle ?? "no_handle",
          calendar_id: calendar?._id,
          calendar_handle: calendar?.handle,
        },
      });

      // Handle downloading reminder again
      const stanzaLink = `${calendar?.stanzaLink}/${user?._id}.ics`;
      const icsContent = generateGCalReminder(calendar?.name, stanzaLink);
      const blob = new Blob([icsContent], {
        type: "text/calendar;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);

      // Create a temporary link to trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.download = `Sync_${calendar?.name}_GCal_Reminder.ics`;

      // Append to the document and trigger the click
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // Track the "Resubscribe" click
      customEvent({
        name: "user_error_resubscribe_calendar",
        category: "CalendarSubscription",
        label: "Resubscribe to Calendar",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          user_handle: user?.handle ?? "no_handle",
          calendar_id: calendar?._id,
          calendar_handle: calendar?.handle,
        },
      });

      // Handle resubscribing to calendar
      handleOpenWebcal(user, getWebcalFormat(calendarApiUrl), calendar);
    }
  };

  const handleCopyToClipboard = () => {
    copyCalendarLinkToClipboard({
      inGroup,
      user,
      calendar,
      showSnackBar,
      setSnackbarOpen,
      setSnackbarMessage,
      setSnackbarSeverity,
    });
  };

  useEffect(() => {
    customEvent({
      name: "landed_on_subscription_verification",
      category: "CalendarSubscription",
      label: "Landed on Subscription Verification",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: calendar?.handle,
      },
    });
  }, []);

  return (
    <>
      {isLoadingCalendarEvents ? (
        <LoadingAnimation />
      ) : (
        <InstructionsLayout
          sx={{
            alignItems: "flex-start",
            textAlign: "left",
          }}
        >
          <Box
            sx={{
              mt: "20%",
            }}
          >
            <Typography variant={"title"}>All Set?</Typography>
            <Typography variant="body1" sx={{ mt: 2, mb: 4 }}>
              {isReminder ? (
                <>
                  Do you see a reminder in your calendar for later today?
                  <br />
                  <br />
                  It should include instructions for subscribing to{" "}
                  {calendar.name} on desktop and syncing on mobile.
                </>
              ) : (
                <>
                  Do you see{" "}
                  <span
                    style={{
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {calendar.name}
                  </span>{" "}
                  in your list of calendars
                  {mostRecentEventDate ? (
                    <>
                      {" "}
                      and an event on{" "}
                      <span
                        style={{
                          fontWeight: 500,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {mostRecentEventDate}
                      </span>
                      ?
                    </>
                  ) : (
                    "?"
                  )}
                </>
              )}
            </Typography>
            <Button
              variant="contained"
              size="grown"
              sx={{ width: "100%", display: "flex", justifySelf: "center" }}
              onClick={handleConfirm}
            >
              YES, I SEE IT
            </Button>
            <Typography
              variant={"subtitle2"}
              sx={{
                mt: 2,
                width: "330px",
                textAlign: "center",
                justifySelf: "center",
              }}
            >
              Having issues?{" "}
              <Link
                onClick={handleResubscribe}
                sx={{ cursor: "pointer", color: "text.secondary" }}
              >
                {isReminder ? "Download the reminder again" : "Resubscribe"}
              </Link>{" "}
              or{" "}
              <Link
                onClick={handleCopyToClipboard}
                sx={{ cursor: "pointer", color: "text.secondary" }}
              >
                copy this link
              </Link>{" "}
              before{" "}
              <Link
                href="https://stanza-1.gitbook.io/stanza-help-center/for-subscribers/editor/issues-redirecting-to-your-calendar-app/manually-import-a-calendar"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ cursor: "pointer", color: "text.secondary" }}
              >
                troubleshooting
              </Link>
              .
            </Typography>
          </Box>
        </InstructionsLayout>
      )}
    </>
  );
};

export default VerifyCalendarSubscription;
